import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { resumePrefill, resumeChat, buildResumeFromDetails, resumeEntitlement, purchaseResumeBuild } from "@/lib/api";
import { completePurchase } from "@/lib/razorpay";
import { TEMPLATES } from "./ResumeTemplates";
import ResumePreview from "./ResumePreview";
import AtsPanel from "./AtsPanel";
import {
  Loader2, Sparkles, Check, ArrowRight, ArrowLeft, Send, Plus, X, User as UserIcon,
  GraduationCap, MessageSquare, LayoutTemplate, FileCheck2, Gift, IndianRupee, Pencil,
} from "lucide-react";

/* The "build a new resume" journey, for freshers:
     1 Confirm  — everything Wishire already knows, prefilled; just fix what's off.
     2 Chat     — 5 quick questions; the assistant extracts facts as you answer.
     3 Template — pick a look.
     4 Build    — the AI writes the resume from YOUR facts only, then preview + download.
   The paywall (first build free, then ₹price) sits at step 4, exactly like before. */

const STEPS = [
  { key: "confirm", label: "Your details", Icon: UserIcon },
  { key: "chat", label: "Quick questions", Icon: MessageSquare },
  { key: "template", label: "Pick a look", Icon: LayoutTemplate },
  { key: "build", label: "Your resume", Icon: FileCheck2 },
];

const TOPIC_LABEL = { internships: "Internships", projects: "Projects", achievements: "Achievements", certifications: "Certifications", extras: "Extras" };
const inputCls = "w-full px-4 py-2.5 rounded-xl border border-stone-200 bg-[#FDFBF7] text-stone-900 placeholder:text-stone-400 focus:outline-none focus:border-stone-900 text-sm";

export default function ResumeWizard({ onExit }) {
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [pre, setPre] = useState(null);
  const [targetRole, setTargetRole] = useState("");
  const [personal, setPersonal] = useState(null);
  const [education, setEducation] = useState([]);
  const [skills, setSkills] = useState([]);
  const [skillInput, setSkillInput] = useState("");
  const [details, setDetails] = useState({});
  const [template, setTemplate] = useState("modern");

  useEffect(() => {
    resumePrefill().then(({ data }) => {
      setPre(data); setTargetRole(data.target_role || ""); setPersonal(data.personal);
      setEducation(data.education?.length ? data.education : [{ degree: "", institution: "", year: "", cgpa: "" }]);
      setSkills(data.skills || []);
    }).catch(() => setPre({ personal: { name: user?.name || "", email: user?.email || "" }, education: [{ degree: "", institution: "", year: "", cgpa: "" }], skills: [] }));
  }, []); // eslint-disable-line

  if (!pre || !personal) return <div className="rounded-3xl border border-stone-200 bg-white p-10 text-stone-500 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Pulling in what we already know about you…</div>;

  const allDetails = { personal, education, skills: [...new Set([...skills, ...(details.skills || [])])], projects: details.projects || [], internships: details.internships || [], certifications: details.certifications || [], achievements: details.achievements || "", extras: details.extras || "" };

  return (
    <div data-testid="resume-wizard">
      {/* Stepper */}
      <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-1" style={{ scrollbarWidth: "none" }}>
        {STEPS.map((s, i) => (
          <React.Fragment key={s.key}>
            <button onClick={() => i < step && setStep(i)} disabled={i > step}
              className={`shrink-0 inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-sm font-medium border transition-colors ${i === step ? "bg-stone-900 text-stone-50 border-stone-900" : i < step ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-white text-stone-400 border-stone-200"}`}>
              <span className="w-5 h-5 rounded-full bg-white/20 inline-flex items-center justify-center text-[11px]">{i < step ? <Check size={12} /> : i + 1}</span>
              {s.label}
            </button>
            {i < STEPS.length - 1 && <span className={`h-px w-6 shrink-0 ${i < step ? "bg-emerald-300" : "bg-stone-200"}`} />}
          </React.Fragment>
        ))}
        <button onClick={onExit} className="ml-auto shrink-0 text-xs text-stone-500 hover:text-stone-900 inline-flex items-center gap-1"><ArrowLeft size={12} /> Back</button>
      </div>

      {step === 0 && (
        <Confirm pre={pre} targetRole={targetRole} setTargetRole={setTargetRole} personal={personal} setPersonal={setPersonal}
          education={education} setEducation={setEducation} skills={skills} setSkills={setSkills} skillInput={skillInput} setSkillInput={setSkillInput}
          onNext={() => setStep(1)} />
      )}
      {step === 1 && <Chat pre={pre} details={details} setDetails={setDetails} onNext={() => setStep(2)} />}
      {step === 2 && <PickTemplate template={template} setTemplate={setTemplate} onNext={() => setStep(3)} onBack={() => setStep(1)} />}
      {step === 3 && <Build details={allDetails} targetRole={targetRole} template={template} onEdit={() => setStep(1)} user={user} />}
    </div>
  );
}

/* ── Step 1 ── */
function Confirm({ pre, targetRole, setTargetRole, personal, setPersonal, education, setEducation, skills, setSkills, skillInput, setSkillInput, onNext }) {
  const setP = (k, v) => setPersonal((p) => ({ ...p, [k]: v }));
  const setE = (i, k, v) => setEducation((rows) => rows.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const addSkill = () => { const parts = skillInput.split(",").map((s) => s.trim()).filter(Boolean); if (parts.length) setSkills((s) => [...new Set([...s, ...parts])]); setSkillInput(""); };
  const ok = personal.name?.trim();
  return (
    <div className="grid lg:grid-cols-3 gap-5">
      <div className="lg:col-span-2 space-y-5">
        <div className="rounded-3xl border border-stone-200 bg-white p-6">
          <div className="flex items-start gap-3 mb-4">
            <span className="p-2 rounded-xl bg-emerald-50 text-emerald-700"><Check size={18} /></span>
            <div><h3 className="font-display text-xl">We've filled this in for you</h3><p className="text-xs text-stone-500 mt-0.5">From your profile{pre.education?.[0]?.institution ? ", your college" : ""}{pre.skills?.length ? " and your LinkedIn/resume" : ""}. Fix anything that's off.</p></div>
          </div>
          <label className="text-xs text-stone-500">Role you're aiming for <span className="text-stone-400">(broad is fine)</span></label>
          <input value={targetRole} onChange={(e) => setTargetRole(e.target.value)} className={`${inputCls} mt-1`} data-testid="rw-role" />
          <div className="grid sm:grid-cols-2 gap-3 mt-4">
            {[["name", "Full name"], ["email", "Email"], ["phone", "Phone"], ["city", "City"], ["linkedin", "LinkedIn URL"], ["github", "GitHub / portfolio URL (optional)"]].map(([k, l]) => (
              <label key={k} className="text-xs text-stone-500">{l}<input value={personal[k] || ""} onChange={(e) => setP(k, e.target.value)} className={`${inputCls} mt-1`} data-testid={`rw-${k}`} /></label>
            ))}
          </div>
          <label className="text-xs text-stone-500 block mt-3">One-line headline <span className="text-stone-400">(optional — e.g. "Final-year BBA student, aspiring marketing associate")</span>
            <input value={personal.headline || ""} onChange={(e) => setP("headline", e.target.value)} className={`${inputCls} mt-1`} /></label>
        </div>

        <div className="rounded-3xl border border-stone-200 bg-white p-6">
          <div className="flex items-start gap-3 mb-4"><span className="p-2 rounded-xl bg-stone-100 text-stone-700"><GraduationCap size={18} /></span><h3 className="font-display text-xl">Education</h3></div>
          {education.map((e, i) => (
            <div key={i} className="grid sm:grid-cols-4 gap-3 mb-3">
              <input placeholder="Degree / course (e.g. BBA)" value={e.degree} onChange={(ev) => setE(i, "degree", ev.target.value)} className={inputCls} data-testid={`rw-degree-${i}`} />
              <input placeholder="College / school" value={e.institution} onChange={(ev) => setE(i, "institution", ev.target.value)} className={inputCls} />
              <input placeholder="Year" value={e.year} onChange={(ev) => setE(i, "year", ev.target.value)} className={inputCls} />
              <input placeholder="CGPA / %" value={e.cgpa} onChange={(ev) => setE(i, "cgpa", ev.target.value)} className={inputCls} />
            </div>
          ))}
          <button onClick={() => setEducation((r) => [...r, { degree: "Class XII", institution: "", year: "", cgpa: "" }])} className="text-sm text-stone-600 hover:text-[#C2410C] inline-flex items-center gap-1"><Plus size={14} /> Add Class XII / X</button>
        </div>

        <div className="rounded-3xl border border-stone-200 bg-white p-6">
          <h3 className="font-display text-xl mb-1">Skills</h3>
          <p className="text-xs text-stone-500 mb-3">Keep the ones you'd be happy to be asked about in an interview.</p>
          <div className="flex flex-wrap gap-2">
            {skills.map((s) => <span key={s} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-stone-100 text-sm">{s}<button onClick={() => setSkills((x) => x.filter((y) => y !== s))} className="text-stone-400 hover:text-red-600"><X size={12} /></button></span>)}
          </div>
          <div className="flex gap-2 mt-3">
            <input value={skillInput} onChange={(e) => setSkillInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addSkill())} placeholder="Add skills, comma-separated" className={inputCls} />
            <button onClick={addSkill} className="px-4 rounded-xl border border-stone-300 text-sm hover:border-stone-900">Add</button>
          </div>
        </div>
      </div>

      <div className="rounded-3xl bg-stone-900 text-stone-50 p-6 h-fit lg:sticky lg:top-6">
        <p className="overline" style={{ color: "rgba(253,251,247,0.55)" }}>What happens next</p>
        <ol className="mt-3 space-y-3 text-sm text-stone-300">
          <li className="flex gap-3"><b className="text-amber-300">2</b> Five quick questions in chat — internships, projects, achievements. "None yet" is a fine answer.</li>
          <li className="flex gap-3"><b className="text-amber-300">3</b> Pick one of three looks.</li>
          <li className="flex gap-3"><b className="text-amber-300">4</b> Your resume, written from your facts only. Download as .docx or PDF.</li>
        </ol>
        <button onClick={onNext} disabled={!ok} data-testid="rw-next-1" className="mt-6 w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-[#C2410C] text-white font-semibold hover:bg-[#9a3412] disabled:opacity-50">Looks right, continue <ArrowRight size={16} /></button>
      </div>
    </div>
  );
}

/* ── Step 2 ── */
function Chat({ pre, details, setDetails, onNext }) {
  const [messages, setMessages] = useState([]);
  const [topicIndex, setTopicIndex] = useState(0);
  const [progress, setProgress] = useState(null);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const scrollRef = useRef(null);

  const call = async (msgs, dets, idx) => {
    setBusy(true); setErr("");
    try {
      const { data } = await resumeChat(msgs.map((m) => ({ role: m.role, text: m.text })), dets, idx);
      setMessages([...msgs, { role: "assistant", text: data.reply }]);
      setDetails(data.details || dets); setTopicIndex(data.topic_index); setProgress(data.progress);
      if (data.done) setDone(true);
    } catch (e) { setErr(e?.response?.data?.detail || "The assistant is unavailable right now — you can skip ahead and add details later."); }
    finally { setBusy(false); }
  };
  useEffect(() => { call([], details, 0); }, []); // eslint-disable-line
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages, busy]);

  const send = (text) => {
    const t = (text ?? input).trim();
    if (!t || busy) return;
    const msgs = [...messages, { role: "user", text: t }];
    setMessages(msgs); setInput("");
    call(msgs, details, topicIndex);
  };

  const counts = { Internships: (details.internships || []).length, Projects: (details.projects || []).length, Certifications: (details.certifications || []).length, Achievements: (details.achievements || "").split("\n").filter(Boolean).length };

  return (
    <div className="grid lg:grid-cols-3 gap-5">
      <div className="lg:col-span-2 rounded-[2rem] border border-stone-200 bg-white overflow-hidden flex flex-col h-[62vh] min-h-[460px]">
        <div className="px-5 py-3 border-b border-stone-100 flex items-center justify-between gap-3">
          <p className="text-sm text-stone-600">{progress ? `Question ${Math.min(progress.covered + 1, progress.total)} of ${progress.total}` : "Starting…"}{progress && <span className="text-stone-400"> · {TOPIC_LABEL[progress.topics[Math.min(topicIndex, progress.total - 1)]]}</span>}</p>
          <div className="flex gap-1">{(progress?.topics || []).map((t, i) => <span key={t} className={`h-1.5 w-6 rounded-full ${i < topicIndex ? "bg-emerald-500" : i === topicIndex && !done ? "bg-[#C2410C]" : "bg-stone-200"}`} />)}</div>
        </div>
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="rw-chat">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${m.role === "user" ? "bg-stone-900 text-stone-50" : "bg-[#F5F3ED] text-stone-800"}`}>{m.text}</div>
            </div>
          ))}
          {busy && <div className="flex justify-start"><div className="rounded-2xl px-4 py-3 bg-[#F5F3ED] text-stone-500 text-sm inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> thinking…</div></div>}
          {err && <p className="text-sm text-red-600">{err}</p>}
        </div>
        <div className="border-t border-stone-100 p-3">
          {done ? (
            <button onClick={onNext} data-testid="rw-next-2" className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-[#C2410C] text-white font-semibold hover:bg-[#9a3412]">That's everything — pick a look <ArrowRight size={16} /></button>
          ) : (
            <>
              <div className="flex gap-2">
                <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())} placeholder="Type your answer…" disabled={busy} data-testid="rw-chat-input"
                  className="flex-1 px-4 py-3 rounded-full border border-stone-300 bg-[#FDFBF7] text-sm focus:outline-none focus:border-stone-900" />
                <button onClick={() => send()} disabled={busy || !input.trim()} className="px-4 rounded-full bg-stone-900 text-stone-50 hover:bg-[#C2410C] disabled:opacity-50"><Send size={16} /></button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                <button onClick={() => send("None yet — skip this one.")} disabled={busy} className="text-xs px-3 py-1.5 rounded-full border border-stone-200 text-stone-600 hover:border-stone-900">None yet, skip</button>
                <button onClick={onNext} disabled={busy} className="text-xs px-3 py-1.5 rounded-full border border-stone-200 text-stone-600 hover:border-stone-900">I'm done, build with what I have</button>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="rounded-3xl border border-stone-200 bg-white p-6 h-fit lg:sticky lg:top-6">
        <p className="overline">Collected so far</p>
        <div className="mt-3 space-y-2">
          {Object.entries(counts).map(([k, v]) => (
            <div key={k} className="flex items-center justify-between text-sm"><span className="text-stone-600">{k}</span><span className={`font-mono text-xs px-2 py-0.5 rounded-full ${v ? "bg-emerald-50 text-emerald-700" : "bg-stone-100 text-stone-400"}`}>{v}</span></div>
          ))}
        </div>
        {(details.projects || []).slice(0, 3).map((p, i) => <p key={i} className="mt-3 text-xs text-stone-600 border-l-2 border-[#C2410C] pl-2"><b>{p.name}</b>{p.tech ? ` · ${p.tech}` : ""}</p>)}
        <p className="mt-5 text-[11px] text-stone-400">Only what you say goes on the resume — the AI polishes wording, never invents experience.</p>
      </div>
    </div>
  );
}

/* ── Step 3 ── */
const THUMB = {
  modern: <div className="flex h-full"><div className="w-1/3 bg-stone-900 p-2 space-y-1.5"><div className="h-2 w-10 bg-amber-300 rounded" /><div className="h-1 w-12 bg-stone-600 rounded" /><div className="h-1 w-8 bg-stone-600 rounded" /><div className="mt-2 h-1 w-10 bg-stone-500 rounded" /><div className="h-1 w-9 bg-stone-500 rounded" /></div><div className="flex-1 p-2 space-y-1.5"><div className="h-1.5 w-16 bg-[#C2410C] rounded" /><div className="h-1 w-full bg-stone-200 rounded" /><div className="h-1 w-5/6 bg-stone-200 rounded" /><div className="mt-2 h-1.5 w-12 bg-[#C2410C] rounded" /><div className="h-1 w-full bg-stone-200 rounded" /><div className="h-1 w-4/6 bg-stone-200 rounded" /></div></div>,
  classic: <div className="h-full p-2 space-y-1.5 text-center"><div className="h-2 w-16 bg-stone-800 rounded mx-auto" /><div className="h-1 w-20 bg-stone-300 rounded mx-auto" /><div className="h-px w-full bg-stone-300 mt-2" /><div className="h-1.5 w-10 bg-stone-700 rounded" /><div className="h-1 w-full bg-stone-200 rounded" /><div className="h-1 w-5/6 bg-stone-200 rounded" /><div className="h-1.5 w-10 bg-stone-700 rounded mt-2" /><div className="h-1 w-full bg-stone-200 rounded" /></div>,
  minimal: <div className="h-full p-2 space-y-1.5 font-mono"><div className="h-2 w-14 bg-stone-900 rounded" /><div className="h-1 w-20 bg-stone-300 rounded" /><div className="mt-2 h-1 w-8 bg-stone-500 rounded" /><div className="h-1 w-full bg-stone-200 rounded" /><div className="h-1 w-5/6 bg-stone-200 rounded" /><div className="mt-2 h-1 w-8 bg-stone-500 rounded" /><div className="h-1 w-full bg-stone-200 rounded" /><div className="h-1 w-3/6 bg-stone-200 rounded" /></div>,
};
const WHO = { modern: "Stands out at startups, marketing, design and product roles.", classic: "Safe for banks, Big-4, government-style and traditional recruiters.", minimal: "Best for ATS-heavy IT services screening (TCS, Infosys, Wipro)." };

function PickTemplate({ template, setTemplate, onNext, onBack }) {
  return (
    <div>
      <h3 className="font-display text-2xl mb-1">Pick a look</h3>
      <p className="text-sm text-stone-500 mb-5">You can switch templates after it's built too — this just sets the default.</p>
      <div className="grid sm:grid-cols-3 gap-4">
        {TEMPLATES.map((t) => (
          <button key={t.id} onClick={() => setTemplate(t.id)} data-testid={`rw-template-${t.id}`}
            className={`text-left rounded-3xl border-2 p-4 transition-colors ${template === t.id ? "border-stone-900 bg-white" : "border-stone-200 bg-white hover:border-stone-400"}`}>
            <div className="aspect-[3/4] rounded-xl border border-stone-200 overflow-hidden bg-[#FDFBF7]">{THUMB[t.id]}</div>
            <div className="mt-3 flex items-center justify-between"><p className="font-display text-lg">{t.label}</p>{template === t.id && <Check size={16} className="text-emerald-600" />}</div>
            <p className="text-xs text-stone-500 mt-0.5">{t.desc}</p>
            <p className="text-xs text-stone-700 mt-2">{WHO[t.id]}</p>
          </button>
        ))}
      </div>
      <div className="mt-6 flex flex-wrap gap-3">
        <button onClick={onNext} data-testid="rw-next-3" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#C2410C] text-white font-semibold hover:bg-[#9a3412]"><Sparkles size={16} /> Build my resume</button>
        <button onClick={onBack} className="inline-flex items-center gap-2 px-5 py-3.5 rounded-full border border-stone-300 text-sm hover:border-stone-900"><ArrowLeft size={14} /> Back to questions</button>
      </div>
    </div>
  );
}

/* ── Step 4 ── */
const STAGES = ["Reading your details", "Writing a summary recruiters skim", "Turning projects into impact bullets", "Checking ATS keywords for your role", "Laying it out"];

function Build({ details, targetRole, template, onEdit, user }) {
  const [result, setResult] = useState(null);
  const [stage, setStage] = useState(0);
  const [err, setErr] = useState("");
  const [ent, setEnt] = useState(null);
  const [payWall, setPayWall] = useState(false);
  const [paying, setPaying] = useState(false);
  const [loading, setLoading] = useState(false);

  const build = async () => {
    setLoading(true); setErr(""); setPayWall(false); setStage(0);
    const timer = setInterval(() => setStage((s) => Math.min(s + 1, STAGES.length - 1)), 2200);
    try {
      const { data } = await buildResumeFromDetails(details, targetRole || undefined);
      setResult(data); if (data.entitlement) setEnt(data.entitlement);
    } catch (e) {
      if (e?.response?.status === 402) setPayWall(true);
      else setErr(e?.response?.data?.detail || "Couldn't build your resume. Please try again.");
    } finally { clearInterval(timer); setLoading(false); }
  };
  useEffect(() => { resumeEntitlement().then(({ data }) => setEnt(data)).catch(() => {}); build(); }, []); // eslint-disable-line

  const payAndBuild = async () => {
    setPaying(true); setErr("");
    try {
      const { data } = await purchaseResumeBuild();
      await completePurchase(data, { user });
      const { data: fresh } = await resumeEntitlement(); setEnt(fresh); setPayWall(false);
      await build();
    } catch (e) { setErr(e?.message || e?.response?.data?.detail || "Payment failed. Please try again."); }
    finally { setPaying(false); }
  };

  if (result) return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div><h3 className="font-display text-2xl inline-flex items-center gap-2"><Check size={20} className="text-emerald-600" /> Your resume is ready</h3><p className="text-sm text-stone-500">Checked against the ATS filters recruiters use. Switch templates, download as .docx, or print to PDF.</p></div>
        <button onClick={onEdit} className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-full border border-stone-300 hover:border-stone-900"><Pencil size={13} /> Edit my answers</button>
      </div>
      {ent?.paid_feature && ent.price_inr > 0 && <p className="text-xs text-stone-500 mb-4 inline-flex items-center gap-1.5">{ent.paid_credits > 0 ? <><Sparkles size={12} /> {ent.paid_credits} paid build{ent.paid_credits > 1 ? "s" : ""} left</> : ent.free_used ? <><IndianRupee size={12} /> Next build ₹{ent.price_inr}</> : <><Gift size={12} /> First build free</>}</p>}
      <div className="mb-5">
        <AtsPanel ats={result.ats} targetRole={targetRole} resumeText={result.resume_text}
          onFixed={(txt, ats) => setResult((r) => ({ ...r, resume_text: txt, ats }))} />
      </div>
      <div className="rounded-3xl border border-stone-200 bg-white p-6 md:p-8" id="built-resume"><ResumePreview structured={result} targetRole={targetRole} initialTemplate={template} /></div>
    </div>
  );

  if (payWall) return (
    <div className="max-w-xl rounded-3xl border border-amber-200 bg-amber-50 p-7" data-testid="resume-paywall">
      <p className="font-display text-2xl text-stone-900">You've used your free resume.</p>
      <p className="text-sm text-stone-600 mt-2">Build this fresh AI resume — new details, new role, new look — for a one-time ₹{ent?.price_inr ?? 199}. Your answers are saved.</p>
      <button onClick={payAndBuild} disabled={paying} data-testid="resume-pay-build" className="mt-5 w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-stone-900 text-stone-50 hover:bg-[#C2410C] disabled:opacity-60 font-medium">
        {paying ? <Loader2 size={16} className="animate-spin" /> : <IndianRupee size={16} />}{paying ? "Processing…" : `Pay ₹${ent?.price_inr ?? 199} & build my resume`}
      </button>
      {err && <p className="mt-3 text-sm text-red-600">{err}</p>}
    </div>
  );

  return (
    <div className="max-w-xl rounded-3xl bg-stone-900 text-stone-50 p-8 wsh-grain relative overflow-hidden">
      <div className="absolute inset-0 opacity-30 pointer-events-none" style={{ background: "radial-gradient(500px 300px at 90% 10%, rgba(194,65,12,0.5), transparent)" }} />
      <div className="relative">
        <p className="overline" style={{ color: "rgba(253,251,247,0.55)" }}>Building</p>
        <h3 className="font-display text-3xl mt-2">{loading ? "Writing your resume…" : err ? "Something went wrong" : "Ready"}</h3>
        <ul className="mt-6 space-y-2.5">
          {STAGES.map((s, i) => (
            <li key={s} className={`flex items-center gap-3 text-sm ${i < stage ? "text-emerald-300" : i === stage && loading ? "text-stone-50" : "text-stone-500"}`}>
              {i < stage ? <Check size={15} /> : i === stage && loading ? <Loader2 size={15} className="animate-spin text-amber-300" /> : <span className="w-[15px] h-[15px] rounded-full border border-stone-600 inline-block" />}{s}
            </li>
          ))}
        </ul>
        {err && <><p className="mt-5 text-sm text-red-300">{err}</p><button onClick={build} className="mt-3 px-5 py-2.5 rounded-full bg-stone-50 text-stone-900 text-sm font-medium">Try again</button></>}
      </div>
    </div>
  );
}
